import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { snapchatIntegrationUrl } from '../../../constants/integrationConstants';
import useGetOrganizationSettings from '../../../apiHooks/queries/useGetOrganizations';
import {GET_PROVIDER_DETAILS, ORGANIZATION_SETTINGS} from '../../../constants/pageIdentifiers';
import {AdProviderIDs, Colors} from '../../../constants/constants';
import SecondaryButton from '../../../components/buttons/secondaryButton';
import useGetSnapchatOrganizations from '../../../apiHooks/queries/snapchatOrganizations/useGetSnapchatOrganizations';
import { useSendSnapchatStore } from '../../../apiHooks/mutations/sendSnapchatOrg/sendSnapchatOrg';
import ProviderNumberBoxes from "../../../components/providerNumberBoxes/providerNumberBoxes";
import useGetAdProviderDetails from "../../../apiHooks/queries/adProviderDetails/useGetAdProviderDetails";

type OrgMap = Record<string, string>;
type AccountMap = Record<string, Record<string, string>>;

const SnapchatPanel = () => {
    const { t } = useTranslation();
    const { data } = useGetOrganizationSettings(ORGANIZATION_SETTINGS);
    const { data: snapchatOrgData } = useGetSnapchatOrganizations('GET_ORGANIZATIONS_SNAPCHAT');
    const { mutate } = useSendSnapchatStore();

    const { data: adProviderDetailsData, isFetching } = useGetAdProviderDetails(
        GET_PROVIDER_DETAILS,
        AdProviderIDs.SNAPCHAT,
        true,
    );

    const organizationSettings = data?.data?.data;

    const organizations: OrgMap = snapchatOrgData?.data?.data?.Organizations || {};
    const adAccounts: AccountMap = snapchatOrgData?.data?.data?.AdAccounts || {};

    const [selectedOrg, setSelectedOrg] = useState('');
    const [selectedAccount, setSelectedAccount] = useState('');

    useEffect(() => {
        if (!organizationSettings) return;

        const orgId = organizationSettings.snapchatOrganizationId;
        const adAccountId = organizationSettings.snapchatAdAccountId;

        if (!orgId) return;

        if (!organizations[orgId] || !adAccounts[orgId]) return;

        setSelectedOrg(orgId);

        if (adAccountId && adAccounts[orgId][adAccountId]) {
            setSelectedAccount(adAccountId);
        }
    }, [organizationSettings, organizations, adAccounts]);


    const currentAccounts = useMemo(() => {
        if (!selectedOrg) return {};
        return adAccounts[selectedOrg] || {};
    }, [selectedOrg, adAccounts]);

    const handleSendToBackend = () => {
        if (!selectedOrg || !selectedAccount) return;

        mutate({
            snapchatOrganizationId: selectedOrg,
            snapchatAdAccount: selectedAccount,
        });
    };

    if (organizationSettings && organizationSettings.isSnapchatActive) {
        return (
            <div className="flex w-full flex-col items-end">
                <div className="flex w-full flex-1 justify-between rounded-xl border p-4">
                    <div className="flex gap-3 align-center w-[720px]">
                        <div className="relative w-full">
                            <select
                                className="
            w-full appearance-none rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm
            shadow-sm transition
            hover:border-gray-300
             focus:outline-none  focus:ring-primary/20
        "
                                value={selectedOrg}
                                onChange={(e) => {
                                    const newOrg = e.target.value;
                                    setSelectedOrg(newOrg);
                                    setSelectedAccount('');
                                }}
                            >
                                <option value="">Select organization</option>
                                {Object.entries(organizations).map(([uuid, name]) => (
                                    <option key={uuid} value={uuid}>
                                        {name}
                                    </option>
                                ))}
                            </select>

                            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div className="relative w-full">
                            <select
                                disabled={!selectedOrg}
                                className={`
            w-full appearance-none rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm
            shadow-sm transition
            hover:border-gray-300
             focus:outline-none  focus:ring-primary/20
            disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed
        `}
                                value={selectedAccount}
                                onChange={(e) => setSelectedAccount(e.target.value)}
                            >
                                <option value="">Select ad account</option>
                                {Object.entries(currentAccounts).map(([uuid, name]) => (
                                    <option key={uuid} value={uuid}>
                                        {name}
                                    </option>
                                ))}
                            </select>

                            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <button
                            onClick={handleSendToBackend}
                            disabled={!selectedOrg || !selectedAccount}
                            className="w-[500px] rounded-lg bg-primary px-4 py-2 text-white text-sm disabled:opacity-50"
                        >
                            Send selection
                        </button>
                    </div>

                    <div className="flex items-center justify-center gap-3 text-xs font-medium text-primary">
                        <div>{t('accountStatus')}</div>
                        <SecondaryButton
                            style={{
                                border: `1px solid ${Colors.green}`,
                                color: Colors.green,
                                height: '40px',
                                fontSize: 14,
                            }}
                        >
                            {t('active')}
                        </SecondaryButton>
                    </div>
                </div>

                <div className="mt-4 flex justify-between gap-4 rounded-xl border p-4 w-full">
                    <ProviderNumberBoxes isLoading={isFetching} tracked stats={adProviderDetailsData?.data?.data?.ads_tracked || 0} />
                    <ProviderNumberBoxes isLoading={isFetching} revenue stats={adProviderDetailsData?.data?.data?.revenue_tracked || 0} />
                    <ProviderNumberBoxes isLoading={isFetching} leads stats={adProviderDetailsData?.data?.data?.leads_attributed || 0} />
                </div>

                <div className="mt-8 flex flex-col justify-center items-center rounded-xl w-full border text-sm overflow-hidden bg-[#E6F4EA] text-primary">
                    <div className="flex items-center w-full justify-between gap-4 py-6 px-4">
                        <div className="flex items-center gap-3">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M10.9997 13V7C10.9997 6.45 11.4497 6 11.9997 6C12.5497 6 12.9997 6.45 12.9997 7V13C12.9997 13.55 12.5497 14 11.9997 14C11.4497 14 10.9997 13.55 10.9997 13ZM11.9997 15C11.1697 15 10.4997 15.67 10.4997 16.5C10.4997 17.33 11.1697 18 11.9997 18C12.8297 18 13.4997 17.33 13.4997 16.5C13.4997 15.67 12.8297 15 11.9997 15ZM21.7897 18.95C22.1497 18.26 22.0097 17.3 21.4197 16.46L13.3397 3.67C13.0497 3.26 12.5497 3 11.9997 3C11.4497 3 10.9497 3.26 10.6397 3.71L2.58974 16.42C1.96974 17.3 1.82974 18.26 2.18974 18.95C2.53974 19.63 3.33974 20 4.42974 20H19.5497C20.6397 20 21.4397 19.63 21.7897 18.95Z"
                                    fill="#4C956C"
                                />
                            </svg>

                            <div>
                                <p className="font-medium">You have connected Snapchat ads</p>
                                <p className="font-light">
                                    Snapchat ads can take up to 48 hours to fully sync up
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="h-1 w-[98%] bg-green rounded-t-xl" />
                </div>
            </div>
        );
    }

    return (
        <div className="flex justify-between gap-5 rounded-xl border p-4">
            <div className="flex flex-col gap-2 text-primary">
                <span className="text-sm font-semibold">Connect to Snapchat</span>
                <p className="text-sm font-normal">
                    Here you can link your Snapchat account with Digger for more efficient tracking.
                </p>
            </div>

            <button
                type="button"
                className="flex items-center gap-2.5 rounded-lg bg-primary px-5 py-2 text-sm text-white"
                onClick={() => {
                    window.location.href = snapchatIntegrationUrl;
                }}
            >
                Connect to Snapchat
            </button>
        </div>
    );
};

export default SnapchatPanel;
